import React, { memo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Button, IconButton, Menu, MenuItem, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { languages } from './i18n';
import LanguageIcon from '@mui/icons-material/Language';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import { HelpModeContext, helpModes, LanguageContext, themeModes } from './App';
import { Brightness4Outlined, Brightness7, PaletteOutlined } from '@mui/icons-material';
import { ChromePicker } from 'react-color';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { coloredMutationTypes, coloredMutationTypesRef } from './colorUtils';
import ExternalLink from './ExternalLink';

const AppFooter = (props) => {

  const { theme, switchTheme } = props;

  const { t } = useTranslation();
  const theTheme = useTheme();

  const [ anchorElForColors, setAnchorElColors ] = useState(null);

  const openColors = (event) => {
    fetchAndInitColors();
    setAnchorElColors(event.currentTarget);
  };

  const closeColors = () => {
    setAnchorElColors(null);
  };

  const [ anchorElForMenuSources, setAnchorElMenuSources ] = useState(null);

  const openMenuSources = (event) => {
    setAnchorElMenuSources(event.currentTarget);
  };

  const closeMenuSources = () => {
    setAnchorElMenuSources(null);
  };

  const [ anchorElForLanguages, setAnchorElForLanguages ] = useState(null);

  const openLanguages = (event) => {
    setAnchorElForLanguages(event.currentTarget);
  };

  const closeLanguages = () => {
    setAnchorElForLanguages(null);
  };

  const [ anchorElForHelpModes, setAnchorElForHelpModes ] = useState(null);

  const openHelpModes = (event) => {
    setAnchorElForHelpModes(event.currentTarget);
  };

  const closeHelpModes = () => {
    setAnchorElForHelpModes(null);
  };

  const [ helpMode, setHelpMode ] = useContext(HelpModeContext);

  const [ language, updateLanguage ] = useContext(LanguageContext);

  const selectLanguage = (selectedLanguage) => {
    updateLanguage(selectedLanguage);
    setAnchorElForLanguages(null);
  };

  const selectHelpMode = (selectedHelpMode) => {
    setHelpMode(selectedHelpMode);
    setAnchorElForHelpModes(null);
  };

  const [ colors, setColors ] = useState({ ...coloredMutationTypesRef });

  const updateColorCssVariable = (hsl, mutationType) => {
    const docEl = document.documentElement;
    const type = mutationType.toLowerCase();
    docEl.style.setProperty(`--${type}-color-hue`, hsl.h);
    docEl.style.setProperty(`--${type}-color-sat`, (hsl.s * 100) + '%');
    docEl.style.setProperty(`--${type}-color-light`, (hsl.l * 100) + '%');
  };

  const onSelectColorFn = (mutationType) => {
    return (selectedColor) => {
      console.log('*** color', selectedColor);
      setColors({ ...colors, [ mutationType ]: { ...selectedColor.hsl } });
      updateColorCssVariable(selectedColor.hsl, mutationType);
    };
  };

  const onSelectColorFns = coloredMutationTypes.reduce((accu, mutationType) => {
    accu[ mutationType ] = onSelectColorFn(mutationType);
    return accu;
  }, {});

  const resetColor = () => {
    Object.keys(coloredMutationTypesRef).forEach(mutationType => {
      updateColorCssVariable(coloredMutationTypesRef[ mutationType ], mutationType);
    });
    fetchAndInitColors();
  };

  const fetchAndInitColors = () => {
    const docEl = document.documentElement;

    coloredMutationTypes.forEach(coloredMutationType => {
      const type = coloredMutationType.toLowerCase();
      const currentHue = getComputedStyle(docEl).getPropertyValue(`--${type}-color-hue`);
      const currentSat = getComputedStyle(docEl).getPropertyValue(`--${type}-color-sat`);
      const currentLight = getComputedStyle(docEl).getPropertyValue(`--${type}-color-light`);
      console.log('*** current ', coloredMutationType, currentHue, currentLight, currentSat);
      colors[ coloredMutationType ] = { h: currentHue, s: currentSat, l: currentLight };
    });

    setColors({ ...colors });
  };

  return (
    <div className={'app-footer'}>

      <IconButton
        aria-label="Languages"
        aria-controls="menu-languages"
        aria-haspopup="true"
        onClick={event => openLanguages(event)}
        size="large">
        <LanguageIcon />
      </IconButton>
      <Menu
        id="menu-languages"
        anchorEl={anchorElForLanguages}
        keepMounted
        open={Boolean(anchorElForLanguages)}
        onClose={closeLanguages}
      >
        <MenuItem onClick={event => selectLanguage(languages.fr)}>
          {t('select-language_fr')}
          {language === languages.fr && <CheckOutlinedIcon fontSize={'small'} />}
        </MenuItem>
        <MenuItem onClick={event => selectLanguage(languages.en)}>
          {t('select-language_en')}
          {language === languages.en && <CheckOutlinedIcon fontSize={'small'} />}
        </MenuItem>
        <MenuItem onClick={event => selectLanguage(languages.br)}>
          {t('select-language_br')}
          {language === languages.br && <CheckOutlinedIcon fontSize={'small'} />}
        </MenuItem>
      </Menu>

      <IconButton
        aria-label="Settings"
        aria-controls="menu-helpModes"
        aria-haspopup="true"
        onClick={event => openHelpModes(event)}
        size="large">
        <SettingsOutlinedIcon />
      </IconButton>
      <Menu
        id="menu-helpModes"
        anchorEl={anchorElForHelpModes}
        keepMounted
        open={Boolean(anchorElForHelpModes)}
        onClose={closeHelpModes}
      >
        <MenuItem onClick={event => selectHelpMode(helpModes.DRAWER)}>
          Help as drawer
          {helpMode === helpModes.DRAWER && <CheckOutlinedIcon fontSize={'small'} />}
        </MenuItem>
        <MenuItem onClick={event => selectHelpMode(helpModes.POPOVER)}>
          Help as popover
          {helpMode === helpModes.POPOVER && <CheckOutlinedIcon fontSize={'small'} />}
        </MenuItem>
        <MenuItem onClick={event => selectHelpMode(helpModes.MODAL)}>
          Help as modal
          {helpMode === helpModes.MODAL && <CheckOutlinedIcon fontSize={'small'} />}
        </MenuItem>
      </Menu>

      <IconButton
        aria-label="Sources"
        aria-controls="menu-sources"
        aria-haspopup="true"
        onClick={event => openMenuSources(event)}
        size="large">
        <CollectionsBookmarkOutlinedIcon />
      </IconButton>
      <Menu
        id="menu-sources"
        anchorEl={anchorElForMenuSources}
        keepMounted
        open={Boolean(anchorElForMenuSources)}
        onClose={closeMenuSources}
      >
        <MenuItem>
          <ExternalLink href={'https://fr.wikiversity.org/wiki/Breton/Grammaire/Mutations'}>
            Wikiversity
          </ExternalLink>
        </MenuItem>
        <MenuItem>
          <ExternalLink href={'https://fr.wikiversity.org/wiki/Breton/Grammaire/Mutations/Synth%C3%A8se'}>
            Wikiversity Synthèse
          </ExternalLink>
        </MenuItem>
        <MenuItem>
          <ExternalLink href={'https://arbres.iker.cnrs.fr/index.php?title=Les_mutations_consonantiques'}>
            Wiki ARBRES
          </ExternalLink>
        </MenuItem>
      </Menu>

      <IconButton
        aria-label="Toggle light/dark theme"
        onClick={event => switchTheme()}
        size="large">
        {theme.palette.mode === themeModes.dark && <Brightness7 />}
        {theme.palette.mode !== themeModes.dark && <Brightness4Outlined />}
      </IconButton>

      <IconButton
        aria-label="Colors"
        aria-controls="menu-colors"
        aria-haspopup="true"
        onClick={event => openColors(event)}
        size="large">
        <PaletteOutlined />
      </IconButton>
      <Popover
        open={Boolean(anchorElForColors)}
        anchorEl={anchorElForColors}
        onClose={closeColors}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <div className={'colors-accordion'} style={{ backgroundColor: theTheme.palette.background.default }}>
          {coloredMutationTypes.map((coloredMutationType, idx) => {
            return (<Accordion key={idx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={`accordion-summary ${coloredMutationType}`}
              >
                <div className={`sample-color ${coloredMutationType}`}></div>
                <span>{t(`modal_title_${coloredMutationType.toLowerCase()}`)}</span>
              </AccordionSummary>
              <AccordionDetails>
                <ChromePicker disableAlpha={true} color={colors[ coloredMutationType ]}
                              onChangeComplete={onSelectColorFns[ coloredMutationType ]} />
              </AccordionDetails>
            </Accordion>);
          })
          }
        </div>
        <Button size={'small'}
                onClick={event => resetColor()}>Reset</Button>
      </Popover>
    </div>
  );
};

export default memo(AppFooter);
